import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import PLink from "@/components/PLink";
import api, { fmtErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import useSEO from "@/hooks/useSEO";
import REGIONS from "@/lib/regions.json";
import { STATES } from "@/components/RegionSelect";
import { toast } from "sonner";
import { Zap, MapPin, Calendar, Send, CloudRain } from "lucide-react";
import { formatDMY, workDatesLabel } from "@/lib/dates";
import PickAttendeeModal from "@/components/PickAttendeeModal";

const TRADES = ["","Carpenter","Electrician","Plumber","Labourer","Plasterer","Roofer","Painter","Bricklayer","Scaffolder","Tiler","HVAC","Concreter","Welder","Landscaper","Gardener","Cleaner","Builder"];

export default function JobsFeed() {
  const nav = useNavigate();
  const { user } = useAuth();
  useSEO({ title: "Last-minute trade jobs across Australia", path: "/jobs", description: "Browse urgent carpentry, electrical, plumbing, labouring and other trade jobs posted by Australian builders. Apply in one tap and get paid through Stripe escrow." });
  const [jobs, setJobs] = useState([]);
  const [trade, setTrade] = useState("");
  const [center, setCenter] = useState("");
  const [radius, setRadius] = useState(50);
  const [loading, setLoading] = useState(true);
  const [applyingId, setApplyingId] = useState(null);
  const [beaconNote, setBeaconNote] = useState(null);

  useEffect(() => {
    const beaconId = new URLSearchParams(window.location.search).get("beacon");
    if (!beaconId || !user) return;
    api.post(`/jobs/beacon/${beaconId}/open`).then(({ data }) => {
      if (data?.beacon) { setBeaconNote(data.beacon); if (data.beacon.trade) setTrade(data.beacon.trade); }
      window.history.replaceState({}, "", "/jobs");
    }).catch(() => {});
  }, [user]);

  const [hasCrew, setHasCrew] = useState(false);
  const [pickFor, setPickFor] = useState(null); // job being applied for when the worker picker is open
  useEffect(() => { if (user) api.get("/crew").then((r) => setHasCrew((r.data?.members || []).length > 0)).catch(() => {}); }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const apply = async (e, jobId, attendeeId = null) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!user) { nav("/login"); return; }
    if (user.role !== "contractor" && !hasCrew) { toast.error("Only tradies can apply for jobs"); return; }
    if (hasCrew && !attendeeId) { setPickFor(jobs.find((j) => j.id === jobId) || { id: jobId }); return; }
    setApplyingId(jobId);
    try {
      await api.post("/requests", { contractor_id: attendeeId || user.id, job_id: jobId });
      setPickFor(null);
      toast.success("Application sent — check your Requests page");
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || "Failed to apply");
    } finally { setApplyingId(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { trade: trade || undefined };
      if (center) { params.center = center; params.radius_km = radius; }
      const { data } = await api.get("/jobs", { params });
      setJobs(data);
    } finally { setLoading(false); }
  }, [trade, center, radius]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
      <div className="flex items-baseline justify-between">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-[#FF6A00]">// LIVE FEED · OPEN JOBS</div>
          <h1 className="mt-1 font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter" data-testid="jobs-feed-title">On-site now</h1>
          {beaconNote && (
            <div className="mt-3 inline-flex items-center gap-2 border border-[#FF6A00]/40 bg-[#FF6A00]/5 px-3 py-2 text-sm text-zinc-200" data-testid="beacon-open-note">
              <Zap className="w-4 h-4 text-[#FF6A00]" /> {beaconNote.builder_name || "A builder"} is after a {beaconNote.trade?.toLowerCase()}{beaconNote.city ? ` near ${beaconNote.city}` : ""} — they've been told you're keen. Apply to a job below or post your availability.
            </div>
          )}
        </div>
        <span className="font-mono-data text-xs text-zinc-500 whitespace-nowrap">{jobs.length} live</span>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="jobs-filters">
        <select value={trade} onChange={(e) => setTrade(e.target.value)}
          className="bg-[#121212] border border-white/15 focus:border-[#FF6A00] focus:outline-none px-3 py-2 text-white text-sm"
          data-testid="filter-trade">
          {TRADES.map((t) => <option key={t} value={t}>{t || "All trades"}</option>)}
        </select>
        <select value={center} onChange={(e) => setCenter(e.target.value)}
          className="bg-[#121212] border border-white/15 focus:border-[#FF6A00] focus:outline-none px-3 py-2 text-white text-sm"
          data-testid="filter-center">
          <option value="">Anywhere</option>
          {STATES.map((s) => <optgroup key={s} label={s}>{REGIONS.filter((r) => r.state === s).map((r) => <option key={r.name} value={r.name}>{r.region}</option>)}</optgroup>)}
        </select>
        <div className="flex items-center gap-2 bg-[#121212] border border-white/15 px-3 py-2">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono-data">{radius} km</span>
          <input type="range" min="5" max="200" step="5" value={radius} onChange={(e) => setRadius(Number(e.target.value))}
            className="flex-1 accent-[#FF6A00]" data-testid="filter-radius" disabled={!center} />
        </div>
        <button onClick={load} className="px-4 py-2 bg-white text-black font-bold uppercase tracking-widest text-xs hover:bg-[#FF6A00]" data-testid="filter-apply-btn">Apply</button>
      </div>

      {loading ? (
        <div className="mt-10 font-mono-data text-zinc-500">Loading…</div>
      ) : jobs.length === 0 ? (
        <div className="mt-10 border border-dashed border-white/15 p-8 sm:p-10 text-center text-zinc-500 font-mono-data" data-testid="jobs-empty">
          No live jobs right now.
          {user?.role === "contractor" && <div className="mt-3 text-sm text-zinc-400 font-sans">You'll get an alert the moment a builder posts one in your trade{user?.trade ? ` (${user.trade})` : ""}.</div>}
          {!user && <div className="mt-3 text-sm text-zinc-400 font-sans">Join as a tradie and we'll alert you the moment a builder posts work in your trade.</div>}
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="jobs-list">
          {jobs.map((j) => (
            <PLink key={j.id} to={`/jobs/${j.id}`}
              className="group border border-white/10 bg-[#121212] p-5 hover:border-[#FF6A00] transition-colors duration-150 flex flex-col"
              data-testid={`job-card-${j.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-mono-data text-[10px] tracking-widest text-zinc-500 uppercase">{j.trade}</span>
                {(j.urgency === "today" || j.urgency === "tomorrow") && (
                  <span className="flex items-center gap-1 bg-[#FF6A00] text-black px-2 py-0.5 text-[10px] font-black uppercase tracking-widest" data-testid={`job-urgent-${j.id}`}>
                    <Zap className="w-3 h-3" /> {j.urgency === "today" ? "Urgent · today" : "Urgent · next-day work"}
                  </span>
                )}
                {j.urgency === "tomorrow" && (
                  <span className="bg-white text-black px-2 py-0.5 text-[10px] font-black uppercase tracking-widest">Tomorrow</span>
                )}
                {j.urgency === "this_week" && (
                  <span className="border border-white/30 text-white px-2 py-0.5 text-[10px] font-black uppercase tracking-widest">This week</span>
                )}
              </div>
              <div className="mt-3 font-display font-black text-xl uppercase leading-tight group-hover:text-[#FF6A00] transition-colors">{j.title}</div>
              <div className="mt-2 text-sm text-zinc-400 line-clamp-2">{j.description}</div>
              <div className="mt-3 flex items-center justify-between text-xs text-zinc-500 font-mono-data">
                <span className="flex items-center gap-1 text-[#FF6A00] font-bold text-sm" data-testid={`job-city-${j.id}`}><MapPin className="w-3.5 h-3.5" /> {j.city}{j.distance_km != null ? <span className="text-zinc-500 font-normal text-xs"> · {j.distance_km}km</span> : ""}</span>
                <span className="flex items-center gap-1" data-testid={`job-dates-${j.id}`}><Calendar className="w-3 h-3" /> {j.flexible && j.end_date ? <span className="text-[#FF6A00]">Anytime {formatDMY(j.start_date)} – {formatDMY(j.end_date)}</span> : (j.work_dates?.length > 1 ? workDatesLabel(j) : formatDMY(j.start_date))}</span>
              </div>
              {j.clash_dates?.length > 0 && (
                <div className="mt-2 text-[11px] font-semibold text-red-500" data-testid={`job-clash-${j.id}`}>
                  You're already booked {j.clash_dates.length === 1 ? "that day" : `on ${j.clash_dates.length} of these days`} ({j.clash_title}) — send a worker or skip this one.
                </div>
              )}
              {(j.weatherproof || j.crew_size > 1) && (
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {j.weatherproof && <span className="inline-flex items-center gap-1 px-2 py-0.5 border border-[#16A34A] text-[#16A34A] text-[10px] uppercase tracking-widest font-bold" data-testid={`job-weather-${j.id}`}><CloudRain className="w-3 h-3" /> Rain or shine</span>}
                  {j.crew_size > 1 && <span className="text-[10px] font-mono-data text-[#FF6A00] uppercase tracking-widest">Crew of {j.crew_size} · {j.booked_count || 0} booked</span>}
                </div>
              )}
              <div className="mt-4 pt-4 border-t border-white/10 flex items-center justify-between gap-2">
                <div>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500 block">{j.billing === "hourly" ? "Hourly job" : "Day rate"}</span>
                  {j.billing === "hourly"
                    ? <span className="font-mono-data text-lg text-white" data-testid={`job-hourly-${j.id}`}>{j.hours}h <span className="text-zinc-500 text-xs">@</span> ${j.hourly_rate}<span className="text-zinc-500 text-xs">/h</span></span>
                    : <span className="font-mono-data text-lg text-white">${j.day_rate}<span className="text-zinc-500 text-xs">/day incl. GST</span></span>}
                </div>
                {(user?.role === "contractor" || (hasCrew && j.builder_id !== user?.id)) && j.status === "open" && (
                  <button
                    onClick={(e) => apply(e, j.id)}
                    disabled={applyingId === j.id}
                    className="inline-flex items-center gap-1 px-3 py-2 bg-[#FF6A00] text-black text-xs font-black uppercase tracking-widest hover:bg-white disabled:opacity-50"
                    data-testid={`job-apply-btn-${j.id}`}
                  >
                    <Send className="w-3.5 h-3.5" /> {applyingId === j.id ? "…" : "Apply"}
                  </button>
                )}
              </div>
            </PLink>
          ))}
        </div>
      )}
      <PickAttendeeModal open={!!pickFor} dates={pickFor?.work_dates?.length ? pickFor.work_dates : [String(pickFor?.start_date || "").slice(0, 10)]} busy={!!applyingId}
        onSubmit={(wid) => apply(null, pickFor.id, wid)} onClose={() => setPickFor(null)} />
    </div>
  );
}
